import React, { useEffect, useState } from "react";
import { CheckOutlined, MinusOutlined } from "@ant-design/icons";
import { Alert, Drawer, Select, Space, Table, Tag, Typography } from "antd";

import {
  getGrantablePermissionScopes,
  teamActions,
  type TeamMemberPermissionConfiguration,
  type TeamMemberPermissionView,
  type TeamMemberProductScope,
} from "@fitness-app/app-store";
import { parsePermissionMap, PermissionKey, PermissionScope, type PermissionScopeEnum } from "@fitness-app/data-models";
import { type TeamMemberWithUser } from "@fitness-app/data-models/entities/TeamMember";

import { usePermission } from "~/hooks/usePermission";
import { matchesPermissionRequirement } from "~/modules/Auth/permissionRequirement";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { permissionLabels, scopeLabels } from "~/modules/Team/permissionLabels";
import { roleEditorAreas, type RoleEditorArea } from "~/modules/Team/pages/TeamRoles/roleEditorAreas";
import { useAppDispatch } from "~/store/initializeStore";

interface TeamMemberPermissionsModalProps {
  member: TeamMemberWithUser | null;
  onClose: () => void;
}

const baselineOption = "BASELINE";

const scopeColors: Record<PermissionScopeEnum, string | undefined> = {
  [PermissionScope.NONE]: undefined,
  [PermissionScope.ASSIGNED]: "blue",
  [PermissionScope.TEAM]: "gold",
  [PermissionScope.ALL]: "green",
};

const getErrorMessage = (error: unknown): string =>
  typeof error === "string" ? error : "Nie udało się pobrać lub zapisać uprawnień.";

const ScopeTag = ({ scope }: { scope: PermissionScopeEnum }) => (
  <Tag color={scopeColors[scope]}>{scopeLabels[scope]}</Tag>
);

export const TeamMemberPermissionsModal = ({
  member,
  onClose,
}: TeamMemberPermissionsModalProps): React.ReactElement | null => {
  const dispatch = useAppDispatch();
  const canManagePermissions = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL);
  const [configuration, setConfiguration] = useState<TeamMemberPermissionConfiguration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [reloadSequence, setReloadSequence] = useState(0);
  const [productScope, setProductScope] = useState<TeamMemberProductScope | null>(null);
  const [savingProducts, setSavingProducts] = useState(false);
  const memberId = member?.id;
  const memberRole = member?.role;

  useEffect(() => {
    let isCurrentRequest = true;

    if (!memberId || !memberRole || !canManagePermissions) {
      setConfiguration(null);
      setError(null);
      return () => {
        isCurrentRequest = false;
      };
    }

    setConfiguration(null);
    setError(null);
    setLoading(true);

    void dispatch(teamActions.fetchTeamMemberProductScope({ memberId }))
      .unwrap()
      .then((result) => {
        if (isCurrentRequest) {
          setProductScope(result);
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setProductScope(null);
        }
      });

    void dispatch(teamActions.fetchTeamMemberPermissionConfiguration({ memberId, role: memberRole }))
      .unwrap()
      .then((result) => {
        if (isCurrentRequest) {
          setConfiguration(result);
        }
      })
      .catch((requestError: unknown) => {
        if (isCurrentRequest) {
          setError(getErrorMessage(requestError));
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [canManagePermissions, dispatch, memberId, memberRole, reloadSequence]);

  if (!member || !canManagePermissions) {
    return null;
  }

  const updateGrant = async (permission: TeamMemberPermissionView, selectedScope: string) => {
    const scope = selectedScope === baselineOption ? null : (selectedScope as PermissionScopeEnum);
    setSavingKey(permission.key);
    setError(null);

    try {
      await dispatch(
        teamActions.setTeamMemberPermissionGrant({
          memberId: member.id,
          key: permission.key,
          scope,
        }),
      ).unwrap();
      setReloadSequence((currentSequence) => currentSequence + 1);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSavingKey(null);
    }
  };

  const renderGrantControl = (permission: TeamMemberPermissionView) => {
    if (!permission.overridable) {
      return <Typography.Text type="secondary">Uprawnienie systemowe</Typography.Text>;
    }

    const grantableScopes = getGrantablePermissionScopes(permission.baselineScope, permission.grantableScopes);

    if (grantableScopes.length === 0 && permission.grantScope === null) {
      return <Typography.Text type="secondary">Maksymalny zakres roli</Typography.Text>;
    }

    return (
      <Select<string>
        aria-label={`Nadpisanie ${permission.key}`}
        className="min-w-44"
        disabled={savingKey !== null}
        loading={savingKey === permission.key}
        value={permission.grantScope ?? baselineOption}
        options={[
          { value: baselineOption, label: "Bez nadpisania" },
          ...grantableScopes.map((scope) => ({ value: scope, label: scopeLabels[scope] })),
        ]}
        onChange={(scope) => void updateGrant(permission, scope)}
      />
    );
  };

  const updateProductScope = async (productIds: string[]) => {
    setSavingProducts(true);
    setError(null);

    try {
      await dispatch(teamActions.setTeamMemberProductScope({ memberId: member.id, productIds })).unwrap();
      setProductScope((current) => (current ? { ...current, assignedProductIds: productIds } : current));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setReloadSequence((currentSequence) => currentSequence + 1);
    } finally {
      setSavingProducts(false);
    }
  };

  // "Przypisani" on products.resources.read means "these products" — without the
  // picker the grant resolves to an empty list and the member sees nothing.
  const resourcesPermission = configuration?.permissions.find(
    (permission) => permission.key === PermissionKey.PRODUCTS_RESOURCES_READ,
  );
  const needsProductScope = resourcesPermission?.effectiveScope === PermissionScope.ASSIGNED;

  const memberName = [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email;

  // Ten sam dwupoziomowy układ co edytor ról: obszary panelu na wierzchu (✓ z
  // uprawnień EFEKTYWNYCH, więc nadpisania od razu widać), klucze po rozwinięciu.
  const effectiveMap = parsePermissionMap(
    Object.fromEntries(
      (configuration?.permissions ?? []).map((permission) => [permission.key, permission.effectiveScope]),
    ),
  );

  const renderPermissionRows = (area: RoleEditorArea) => (
    <Table<TeamMemberPermissionView>
      dataSource={(configuration?.permissions ?? []).filter((permission) =>
        (area.keys as readonly string[]).includes(permission.key),
      )}
      pagination={false}
      rowKey="key"
      size="small"
    >
      <Table.Column<TeamMemberPermissionView>
        title="Uprawnienie"
        key="permission"
        render={(_, permission) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{permissionLabels[permission.key] ?? permission.description}</Typography.Text>
            <Typography.Text type="secondary" className="text-xs">
              {permission.key}
            </Typography.Text>
          </Space>
        )}
      />
      <Table.Column<TeamMemberPermissionView>
        title="Zakres roli"
        key="baseline"
        width={130}
        render={(_, permission) => <ScopeTag scope={permission.baselineScope} />}
      />
      <Table.Column<TeamMemberPermissionView>
        title="Grant indywidualny"
        key="grant"
        width={220}
        render={(_, permission) => renderGrantControl(permission)}
      />
      <Table.Column<TeamMemberPermissionView>
        title="Efektywnie"
        key="effective"
        width={130}
        render={(_, permission) => <ScopeTag scope={permission.effectiveScope} />}
      />
    </Table>
  );

  return (
    <Drawer title={`Uprawnienia: ${memberName}`} open onClose={onClose} width={920} destroyOnHidden>
      <Space direction="vertical" size="middle" className="w-full">
        <Typography.Paragraph type="secondary">
          Nadpisanie może tylko zwiększyć zakres wynikający z roli. Aby cofnąć grant, wybierz „Bez nadpisania”.
        </Typography.Paragraph>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <Table<RoleEditorArea>
          dataSource={[...roleEditorAreas]}
          loading={loading}
          pagination={false}
          rowKey="area"
          size="small"
          expandable={{ expandedRowRender: renderPermissionRows }}
        >
          <Table.Column<RoleEditorArea> title="Obszar" key="label" dataIndex="label" />
          <Table.Column<RoleEditorArea>
            title="Widzi podstronę"
            key="visible"
            width={140}
            align="center"
            render={(_, area) =>
              matchesPermissionRequirement(effectiveMap, dashboardPermissionRequirements[area.area]) ? (
                <CheckOutlined style={{ color: "#25b10f" }} />
              ) : (
                <MinusOutlined style={{ color: "#bbb" }} />
              )
            }
          />
        </Table>

        {needsProductScope ? (
          <Space direction="vertical" size={4} className="w-full">
            <Typography.Text strong>Produkty z dostępem do materiałów</Typography.Text>
            <Typography.Text type="secondary" className="text-xs">
              Zakres „Przypisani" otwiera materiały tylko wybranych produktów. Pusta lista oznacza brak dostępu.
            </Typography.Text>
            <Select<string[]>
              mode="multiple"
              allowClear
              className="w-full"
              aria-label="Produkty z dostępem do materiałów"
              placeholder="Wybierz produkty"
              loading={savingProducts}
              disabled={savingProducts || !productScope}
              value={productScope?.assignedProductIds ?? []}
              optionFilterProp="label"
              options={(productScope?.products ?? []).map((product) => ({
                value: product.id,
                label: product.name,
              }))}
              onChange={(productIds) => void updateProductScope(productIds)}
            />
          </Space>
        ) : null}
      </Space>
    </Drawer>
  );
};
